'use client'

import { useState, useEffect } from 'react'

/**
 * Hook to track online/offline status
 *
 * @returns boolean indicating if the browser is online
 *
 * Usage:
 * ```tsx
 * const isOnline = useOnlineStatus()
 * if (!isOnline) {
 *   return <OfflineBanner />
 * }
 * ```
 */
export function useOnlineStatus(): boolean {
  // Default to true to avoid flash of offline state during SSR/hydration
  const [isOnline, setIsOnline] = useState(true)

  useEffect(() => {
    // Set initial state from navigator (only runs client-side)
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      setIsOnline(navigator.onLine)
    }

    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return isOnline
}
